import React, { Component } from 'react';
// eslint-disable-next-line
// @ts-ignore
import Stats from 'stats.js';
import cn from 'classnames';

import { thumbnailShortTransformation, thumbnailFullTransformation } from '../../constants/detection';
import VideoStream from '../../helpers/VideoStream';
import FaceDetection from '../../helpers/FaceDetection';
import DetectionResult from '../../helpers/DetectionResult';
import workers from '../../workers';

import styles from './RGBVideo.module.css';

interface RGBVideoProps {
  width: number;
  height: number;
  videoDeviceIndex: number;
  videoDeviceTrack: number;
  backend: string;
  maskDetectionsSettings: any;
  faceDetectionSettings: any;
  boundingBoxShow: boolean;
  startThermalProcess: any;
  clearThermalSolutionBox: any;
  isStartThermalProcess: boolean;
  isClearThermalSolutionBox: boolean;
}

interface RGBVideoState {
  isWithMask: boolean;
  maskScore: number;
  isFaceDetected: boolean;
}

class RGBVideo extends Component<RGBVideoProps, RGBVideoState> {
  private refVideo: any;
  private boundingBox: any;
  private resizedFrameSnapshot: any;
  private resizedFrameSnapshotCtx: any;
  private VideoStream: VideoStream;
  private lastImage: any = null;
  private imageRatio: any = null;
  private faceDetection: FaceDetection = new FaceDetection();
  private faceDetectionWorker: any;
  private boundingBoxCtx: any;
  private maskDetectionWorker: any;
  private detectedThumbnailSnapshot: any;
  private detectedThumbnailSnapshotCtx: any;
  private isMaskWorkerInProgress: boolean;
  private isFaceWorkerInProgress: boolean;
  private metricsRef: any;
  private grabFrameFPS: any;
  private faceProcessingFPS: any;
  private faceProcessingMS: any;
  private faceFPS: any;
  private faceMS: any;
  private maskProcessingFPS: any;
  private maskProcessingMS: any;
  private maskFPS: any;
  private maskMS: any;
  private statsMB: any;
  private processLastFrameAnimationRequested = false;
  private processLastDetectedPredictionsAnimationRequested = false;
  private maskDetectionResult: DetectionResult;

  constructor(props: any) {
    super(props);

    this.state = {
      isWithMask: false,
      maskScore: 0,
      isFaceDetected: false,
    };

    const { width, height, videoDeviceIndex, videoDeviceTrack, faceDetectionSettings } = props;

    this.refVideo = React.createRef();
    this.resizedFrameSnapshot = React.createRef();
    this.boundingBox = React.createRef();
    this.metricsRef = React.createRef();

    this.VideoStream = new VideoStream(width, height, videoDeviceIndex, videoDeviceTrack);
    this.faceDetection = new FaceDetection();
    this.maskDetectionResult = new DetectionResult(faceDetectionSettings.solutionBoxSize);
    this.faceDetectionWorker = workers.FaceDetectionWorkerFactory.createWebWorker();
    this.maskDetectionWorker = workers.MaskDetectionWorkerFactory.createWebWorker();

    this.isFaceWorkerInProgress = false;
    this.isMaskWorkerInProgress = false;

    // Grab frame
    this.grabFrameFPS = new Stats();
    this.grabFrameFPS.showPanel(0);

    // Face detection
    this.faceProcessingFPS = new Stats();
    this.faceProcessingFPS.showPanel(0);

    this.faceProcessingMS = new Stats();
    this.faceProcessingMS.showPanel(1);

    this.faceFPS = new Stats();
    this.faceFPS.showPanel(0);

    this.faceMS = new Stats();
    this.faceMS.showPanel(1);

    // Mask detection
    this.maskProcessingFPS = new Stats();
    this.maskProcessingFPS.showPanel(0);

    this.maskProcessingMS = new Stats();
    this.maskProcessingMS.showPanel(1);

    this.maskFPS = new Stats();
    this.maskFPS.showPanel(0);

    this.maskMS = new Stats();
    this.maskMS.showPanel(1);

    // Memory
    this.statsMB = new Stats();
    this.statsMB.showPanel(2);
  }

  showMetrics = () => {
    // Main thread
    const divMainThreadGrabFrameFPS = document.createElement('div');
    divMainThreadGrabFrameFPS.innerText = 'Grab frame, FPS';
    divMainThreadGrabFrameFPS.append(this.grabFrameFPS.dom);
    this.metricsRef.append(divMainThreadGrabFrameFPS);

    // Face detection
    const divFaceProcessingFPS = document.createElement('div');
    divFaceProcessingFPS.innerText = 'Face processing, FPS';
    divFaceProcessingFPS.append(this.faceProcessingFPS.dom);
    this.metricsRef.append(divFaceProcessingFPS);

    const divFaceProcessingMS = document.createElement('div');
    divFaceProcessingMS.innerText = 'Face processing, MS';
    divFaceProcessingMS.append(this.faceProcessingMS.dom);
    this.metricsRef.append(divFaceProcessingMS);

    const divFaceDetectionFPS = document.createElement('div');
    divFaceDetectionFPS.innerText = 'Face detection worker, FPS';
    divFaceDetectionFPS.append(this.faceFPS.dom);
    this.metricsRef.append(divFaceDetectionFPS);

    const divFaceDetectionMS = document.createElement('div');
    divFaceDetectionMS.innerText = 'Face detection worker, MS';
    divFaceDetectionMS.append(this.faceMS.dom);
    this.metricsRef.append(divFaceDetectionMS);

    // Mask detection
    const divMaskProcessingFPS = document.createElement('div');
    divMaskProcessingFPS.innerText = 'Mask processing, FPS';
    divMaskProcessingFPS.append(this.maskProcessingFPS.dom);
    this.metricsRef.append(divMaskProcessingFPS);

    const divMaskProcessingMS = document.createElement('div');
    divMaskProcessingMS.innerText = 'Mask processing, MS';
    divMaskProcessingMS.append(this.maskProcessingMS.dom);
    this.metricsRef.append(divMaskProcessingMS);

    const divMaskDetectionFPS = document.createElement('div');
    divMaskDetectionFPS.innerText = 'Mask detection worker, FPS';
    divMaskDetectionFPS.append(this.maskFPS.dom);
    this.metricsRef.append(divMaskDetectionFPS);

    const divMaskDetectionMS = document.createElement('div');
    divMaskDetectionMS.innerText = 'Mask detection worker, MS';
    divMaskDetectionMS.append(this.maskMS.dom);
    this.metricsRef.append(divMaskDetectionMS);

    // Total memory
    const divMainThreadMB = document.createElement('div');
    divMainThreadMB.innerText = 'Total memory, MB';
    divMainThreadMB.append(this.statsMB.dom);
    this.metricsRef.append(divMainThreadMB);
  };

  async componentDidMount() {
    const { faceDetectionSettings, maskDetectionsSettings, backend } = this.props;

    this.showMetrics();

    await Promise.all([this.faceDetectionWorker.load(), this.maskDetectionWorker.load()]);
    await Promise.all([
      this.faceDetectionWorker.init({
        backend,
        faceDetectionSettings,
      }),
      this.maskDetectionWorker.init({
        maskDetectionsSettings,
        backend,
      }),
    ]);

    // It can be situations when we try to get stream(s) simultaniously for RGB and Thermal cameras and it doesn't return stream
    // To mitigate the error we just try to get stream the second/third time
    const stream = await this.VideoStream.getStream();

    this.refVideo.srcObject = stream;
    this.refVideo.play();

    this.resizedFrameSnapshotCtx = this.resizedFrameSnapshot.getContext('2d');
    this.detectedThumbnailSnapshotCtx = this.detectedThumbnailSnapshot.getContext('2d');

    this.refVideo.onloadedmetadata = async () => {
      const videoWidth = this.refVideo.videoWidth;
      const videoHeight = this.refVideo.videoHeight;

      this.refVideo.width = videoWidth;
      this.refVideo.height = videoHeight;

      this.boundingBox.width = videoWidth;
      this.boundingBox.height = videoHeight;
      this.boundingBoxCtx = this.boundingBox.getContext('2d');

      this.imageRatio = this.faceDetection.calculateFaceDetectionImageRatio(
        videoWidth,
        videoHeight,
        faceDetectionSettings.faceDetectionImageMinWidth,
        faceDetectionSettings.faceDetectionImageMinHeight,
      );

      this.resizedFrameSnapshot.width = this.imageRatio.faceDetectionImageWidth;
      this.resizedFrameSnapshot.height = this.imageRatio.faceDetectionImageHeight;

      await this.grabFrame();
      await this.processLastFrame();
    };
  }

  requestNextIteration = (func: any) => {
    setTimeout(func, 0);
    // requestAnimationFrame(func);
  };

  grabFrame = async () => {
    try {
      this.grabFrameFPS.begin();
      await this.VideoStream.grabFrame();
      this.grabFrameFPS.end();

      if (!this.processLastFrameAnimationRequested && !this.isFaceWorkerInProgress) {
        this.requestNextIteration(this.processLastFrame);
        this.processLastFrameAnimationRequested = true;
      }
    } finally {
      this.requestNextIteration(this.grabFrame);
    }
  };

  processLastFrame = async () => {
    this.faceProcessingFPS.begin();
    this.faceProcessingMS.begin();
    this.statsMB.begin();

    this.processLastFrameAnimationRequested = false;

    if (!this.isFaceWorkerInProgress) {
      this.lastImage = this.VideoStream.getImage();
      this.maskDetectionResult.addData({
        imageDataToProcess: this.lastImage,
      });

      if (this.lastImage) {
        this.faceFPS.begin();
        this.faceMS.begin();

        this.isFaceWorkerInProgress = true;
        this.resizedFrameSnapshotCtx.drawImage(
          this.lastImage,
          0,
          0,
          this.imageRatio.faceDetectionImageWidth,
          this.imageRatio.faceDetectionImageHeight,
        );
        const resizedImageDataToProcess = this.resizedFrameSnapshotCtx.getImageData(
          0,
          0,
          this.resizedFrameSnapshot.width,
          this.resizedFrameSnapshot.height,
        );
        const lastItem = this.maskDetectionResult.getLast();

        this.faceDetectionWorker
          .detectFace({
            imageDataToProcess: resizedImageDataToProcess,
            faceDetectionImageRatio: this.imageRatio.faceDetectionImageRatio,
            videoWidth: this.imageRatio.videoWidth,
            videoHeight: this.imageRatio.videoHeight,
            lastIndex: lastItem.index,
          })
          // eslint-disable-next-line
          // @ts-ignore
          .then(({ predictions, lastIndex }) => {
            this.maskDetectionResult.updateDataByIndex(lastIndex, 'predictions', predictions);
            this.maskDetectionResult.updateDataByIndex(lastIndex, 'imageDataToProcess', resizedImageDataToProcess);

            const isFaceDetected = this.maskDetectionResult.isFaceDetected();

            if (this.props.isStartThermalProcess !== isFaceDetected) {
              this.props.startThermalProcess(isFaceDetected);
            }

            if (!isFaceDetected && this.props.isClearThermalSolutionBox !== isFaceDetected) {
              this.props.clearThermalSolutionBox(!isFaceDetected);
            }

            this.setState({
              isFaceDetected,
            });

            this.drawOutputMetadata(predictions);

            if (predictions.length === 1 && !this.processLastDetectedPredictionsAnimationRequested) {
              this.processLastDetectedPredictionsAnimationRequested = true;
              this.requestNextIteration(this.processLastDetectedPredictions);
            } else {
              this.detectedThumbnailSnapshotCtx.clearRect(
                0,
                0,
                this.detectedThumbnailSnapshot.width,
                this.detectedThumbnailSnapshot.height,
              );
            }
          })
          .finally(() => {
            this.isFaceWorkerInProgress = false;

            this.faceFPS.end();
            this.faceMS.end();
          });
      }
    }

    this.faceProcessingFPS.end();
    this.faceProcessingMS.end();
    this.statsMB.end();
  };

  processLastDetectedPredictions = async () => {
    this.maskProcessingFPS.begin();
    this.maskProcessingMS.begin();
    this.statsMB.begin();

    this.processLastDetectedPredictionsAnimationRequested = false;

    const lastItem = this.maskDetectionResult.getLast();

    if (!this.isMaskWorkerInProgress && lastItem.data.predictions.length === 1 && !lastItem.data.maskDetection) {
      this.maskFPS.begin();
      this.maskMS.begin();

      const { maskDetectionsSettings } = this.props;
      this.isMaskWorkerInProgress = true;

      this.maskDetectionWorker
        .detectMask({
          predictions: lastItem.data.predictions,
          imageDataToProcess: lastItem.data.imageDataToProcess,
          thumbnailShortTransformation,
          thumbnailFullTransformation,
          thumbnailSizeForMask: maskDetectionsSettings.thumbnailSizeForMask,
          faceDetectionImageWidth: this.imageRatio.faceDetectionImageWidth,
          imageTransformType: 'full',
          lastIndex: lastItem.index,
        })
        .then((masksData: any) => {
          this.detectedThumbnailSnapshot.width = masksData.imgData.width;
          this.detectedThumbnailSnapshot.height = masksData.imgData.height;
          this.detectedThumbnailSnapshotCtx.putImageData(masksData.imgData, 0, 0);

          const maskDetectionResult = {
            isWithMask: masksData.masksScores[0] > 0.5,
            maskScore: masksData.masksScores[0],
          };
          this.maskDetectionResult.updateDataByIndex(masksData.lastIndex, 'maskDetection', maskDetectionResult);

          this.setState({
            isWithMask: this.maskDetectionResult.isMaskDetected(),
            maskScore: masksData.masksScores[0],
          });
        })
        .finally(() => {
          this.isMaskWorkerInProgress = false;

          this.maskFPS.end();
          this.maskMS.end();
        });
    }

    this.maskProcessingFPS.end();
    this.maskProcessingMS.end();
    this.statsMB.end();
  };

  drawOutputMetadata = (predictions: any) => {
    const { boundingBoxShow } = this.props;

    this.boundingBoxCtx.clearRect(0, 0, this.boundingBox.width, this.boundingBox.height);
    if (predictions && boundingBoxShow) {
      for (let i = 0; i < predictions.length; i++) {
        const topLeftPrediction = predictions[i].originalTopLeft;
        const bottomRightPrediction = predictions[i].originalBottomRight;
        const predictionSize = [
          bottomRightPrediction[0] - topLeftPrediction[0],
          bottomRightPrediction[1] - topLeftPrediction[1],
        ];
        this.boundingBoxCtx.fillStyle = 'rgba(255, 0, 0, 0.5)';
        this.boundingBoxCtx.fillRect(topLeftPrediction[0], topLeftPrediction[1], predictionSize[0], predictionSize[1]);

        // this.boundingBoxCtx.fillStyle = 'blue';
        // const landmarks = predictions[i].originalLandmarks;
        // for (let j = 0; j < landmarks.length; j++) {
        //   const x = landmarks[j][0];
        //   const y = landmarks[j][1];
        //   this.boundingBoxCtx.fillRect(x, y, 5, 5);
        // }
      }
    }
  };

  render() {
    const { isFaceDetected = null, isWithMask, maskScore } = this.state;
    return (
      <>
        <div
          ref={(metrics: any) => {
            this.metricsRef = metrics;
          }}
          className={styles.metrics}
        />
        <div>
          With mask: {isWithMask || isWithMask !== null ? isWithMask.toString() : 'N/A'} | Score:{' '}
          {maskScore ? maskScore : 'N/A'}
        </div>
        <div className={styles.videoWrapper}>
          <video
            ref={(player: any) => {
              this.refVideo = player;
            }}
            className={cn(styles.RGBVideo, {
              [styles.nocolor]: isFaceDetected === null,
              [styles.red]: !isFaceDetected && isFaceDetected !== null,
              [styles.yellow]: isFaceDetected && isWithMask,
              [styles.green]: isFaceDetected && !isWithMask,
            })}
          />
          <canvas
            ref={(canvas: any) => {
              this.boundingBox = canvas;
            }}
            className={styles.boundingBoxes}
            // hidden
          />
          <canvas
            ref={(canvas: any) => {
              this.resizedFrameSnapshot = canvas;
            }}
            className={cn(styles.RGBVideo, {
              [styles.nocolor]: true,
            })}
            // hidden
          />
          <canvas
            ref={(canvas: any) => {
              this.detectedThumbnailSnapshot = canvas;
            }}
            className={cn(styles.RGBVideo, {
              [styles.nocolor]: true,
            })}
            // hidden
          />
        </div>
      </>
    );
  }
}

export default RGBVideo;
